/**
 * THE 59 PIXELS — 27 Sep 2026. Read this before changing anything here or in
 * `coverViewport()` in utils/homeScreen.ts.
 *
 * **Growing the viewport after the page has laid out leaves iOS holding a
 * scroll offset it cannot reconcile, and it settles on a NEGATIVE one.**
 *
 * Portal OS has no index.html to put a meta tag in, so `coverViewport()`
 * patches `viewport-fit=cover` into the host page's viewport meta at runtime
 * — which it must, or every `env(safe-area-inset-*)` reads 0 and the
 * installed app puts its wordmark in the clock. On Tom's installed iPhone 15
 * Pro the page loads at a layout viewport of 793 (the 852 screen minus the
 * 59pt status bar), the patch lands, the viewport becomes the full 852, and
 * the document is left parked at **scrollY = -59**. 852 - 793 = 59. Nothing
 * ever scrolls it back, so it stays there for the life of the session.
 *
 * What that cost before anyone found it: iOS carries `position: fixed` with
 * that displacement, so the phone's tab bar reported a rect of 816–911
 * against an 852-tall screen. Its bottom 59px hung off the screen, and the
 * strip where the labels appear at rest was occupied by the Week Ahead day
 * row behind it — a full-width button that opens the Calendar. Every tab
 * "jumped to the Calendar" on the first touch of a session because no tab
 * was ever being pressed.
 *
 * **A negative document scroll is never meaningful in this app.** Below xl
 * the studio shell is a fixed-height column that does not scroll at all;
 * above xl the document only ever scrolls downwards. So: put it back, and
 * keep putting it back.
 *
 * WHY SO MANY ATTEMPTS. The reflow after a viewport change is asynchronous
 * and iOS does not promise when it lands — two animation frames was a guess,
 * and guessing is what made this bug take a day. The schedule below covers a
 * slow phone without polling forever: two frames, then 50/150/400/1000ms,
 * then nothing. Each attempt is a comparison and, almost never, a
 * `scrollTo`. After that it only reacts to events that can actually change
 * the viewport again.
 *
 * It is deliberately NOT a permanent interval. A timer that runs all day to
 * correct a state that occurs once is the kind of thing nobody dares delete
 * later.
 */

const RETRIES_MS = [50, 150, 400, 1000]

function pinToTop() {
  if (typeof window === 'undefined') return
  /* Only ever corrects upwards-past-the-top. A real downward scroll, on a
     laptop or inside any scroller, is left completely alone. */
  if (window.scrollY < 0) window.scrollTo(0, 0)
}

let installed = false

/** Safe to call more than once; only the first call does anything. */
export function keepDocumentPinned(): void {
  if (typeof window === 'undefined' || installed) return
  installed = true

  pinToTop()
  requestAnimationFrame(() => {
    pinToTop()
    requestAnimationFrame(pinToTop)
  })
  for (const delay of RETRIES_MS) window.setTimeout(pinToTop, delay)

  /* Everything that can change the viewport again: rotating, the keyboard,
     coming back to the foreground, or being restored from the page cache. */
  window.addEventListener('resize', pinToTop)
  window.addEventListener('orientationchange', pinToTop)
  window.addEventListener('pageshow', pinToTop)
  document.addEventListener('visibilitychange', pinToTop)
  window.visualViewport?.addEventListener('resize', pinToTop)
  window.visualViewport?.addEventListener('scroll', pinToTop)
}
